// catalog_intersect 7b5fc12f-88af-4921-af9f-70c9f9287950, updated 73-cosmetic bundle.
// Unity receives category/key separately; the slash-form ID is `${category}/${key}`.
export const CLAY_AVATAR = 'Avatar_IntersectClay';
export type ClayPair = { category: string; key: string };
export const CLAY_CATALOG: Record<string, readonly string[]> = {
  'skin-colours': ['brown-body', 'tan-body', 'white-body'],
  eyes: ['blue-eyes', 'brown-eyes', 'eye-patch', 'green-eyes', 'intersect-shades', 'normal-eyes', 'side-eye', 'third-eye'],
  eyebrows: ['blue-eyebrows', 'normal-eyebrows', 'one-raised-eyebrow', 'raised-eyebrows'],
  clothes: ['90s-jacket', 'armour', 'blue-flannel', 'head-logo-shirt', 'intersect-hoodie', 'intersect-shirt', 'intersect-tracksuit', 'intersect-vest', 'ladder-top', 'leather-jacket', 'robot-body', 'tshirt-grey', 'turtleneck', 'white-vest'],
  accesories: ['blue-frog', 'dog-collar', 'septum', 'white-stud'],
  'back-accesories': ['intersect-flag'],
  'hats-and-hair': ['anime-hair', 'blonde-long-hair', 'blue-fringe', 'blue-mohawk', 'brown-bob', 'brown-fade', 'buzzcut-black', 'fringe', 'hand-head', 'head-ladders', 'intersect-braids', 'intersect-halo-n-horns', 'intersect-hat', 'intersect-hat-and-black-mulle', 'intersect-hat-and-blonde-hair', 'intersect-hat-and-blonde-mullet', 'intersect-hat-long-brown-hair', 'intersect-hat-with-dreads', 'mid-length-blonde-hair', 'wizard-hat'],
  mouths: ['beard', ...['brown', 'tan', 'white'].flatMap(tone => ['big-grin', 'drippy-tongue', 'normal-mouth', 'screaming', 'smile', 'stash'].map(mouth => `${tone}-${mouth}`))],
};

export function randomClayTraits(random: () => number = Math.random): ClayPair[] {
  const pick = (values: readonly string[]) => values[Math.floor(random() * values.length)];
  const skin = pick(CLAY_CATALOG['skin-colours']);
  const tone = skin.split('-')[0];
  const pairs = [{ category: 'skin-colours', key: skin }];
  for (const category of ['eyes', 'eyebrows', 'clothes', 'accesories']) {
    pairs.push({ category, key: pick(CLAY_CATALOG[category]) });
  }
  pairs.push({ category: 'mouths', key: random() < .05 ? 'beard' : pick(CLAY_CATALOG.mouths.filter(key => key.startsWith(`${tone}-`))) });
  if (random() >= .05) pairs.push({ category: 'hats-and-hair', key: pick(CLAY_CATALOG['hats-and-hair']) });
  if (random() < .10) pairs.push({ category: 'back-accesories', key: 'intersect-flag' });
  return pairs;
}

const aliases: Record<string, string> = {
  accessories: 'accesories', back_accessories: 'back-accesories', back_accesories: 'back-accesories',
  skin_colours: 'skin-colours', hats_and_hair: 'hats-and-hair',
};

/** Accept the partner's category object and legacy array without guessing unavailable assets. */
export function mapClayTraits(payload: unknown): { pairs: ClayPair[]; unsupported: string[] } {
  const values = Array.isArray(payload) ? payload : payload && typeof payload === 'object' ? Object.values(payload) : [];
  const selected = new Map<string, string>();
  const unsupported: string[] = [];
  for (const value of values) {
    if (typeof value !== 'string' || !value.trim() || value === 'none') continue;
    const raw = value.replace(/^clay-avatar-traits\//, '');
    if (/^backgrounds?[_/]/.test(raw)) continue;
    const categories = [...Object.keys(CLAY_CATALOG), ...Object.keys(aliases)].sort((a, b) => b.length - a.length);
    const prefix = categories.find(category => raw.startsWith(`${category}_`) || raw.startsWith(`${category}/`));
    if (!prefix) { unsupported.push(value); continue; }
    const category = aliases[prefix] || prefix;
    let key = raw.slice(prefix.length + 1);
    if (category === 'mouths') {
      key = key.replace(/^(brown|tan|white)[_/]/, '$1-').replace('drippy-tounge', 'drippy-tongue');
      if (key === 'wizard-beard' || key === 'wizard-beard/beard') key = 'beard';
    }
    if (category === 'hats-and-hair') {
      if (key === 'head-hand') key = 'hand-head';
      if (key === 'intersect-hat-and-long-brown-hair') key = 'intersect-hat-long-brown-hair';
    }
    if (category === 'eyebrows' && key === 'normal-eyebrow') key = 'normal-eyebrows';
    if (!CLAY_CATALOG[category].includes(key)) { unsupported.push(value); continue; }
    selected.set(category, key);
  }
  const pairs = Array.from(selected).map(([category, key]) => ({ category, key }));
  pairs.sort((a, b) => Number(b.category === 'skin-colours') - Number(a.category === 'skin-colours'));
  return { pairs, unsupported };
}
